import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

function HomeJsonLd() {
  useEffect(() => {
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': ['LocalBusiness', 'ProfessionalService'],
        '@id': `${SITE_URL}/#business`,
        name: 'KAZU.PHOTO',
        description: '石川県金沢市を拠点に活動するフォトグラファー。家族写真・ウェディング・ポートレート・商業撮影・ドキュメンタリー映像制作。',
        url: SITE_URL,
        image: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/e4341d1a0d62cc6d480c2d41583be8be.jpeg',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '金沢市',
          addressRegion: '石川県',
          addressCountry: 'JP',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 36.5613,
          longitude: 136.6562,
        },
        areaServed: {
          '@type': 'State',
          name: '石川県',
        },
        sameAs: [
          'https://www.instagram.com/ka91uver/',
          'https://www.youtube.com/channel/UChcXY6jhxBz8Q74T6IYq2MA',
        ],
        priceRange: '¥¥',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'KAZU.PHOTO',
        description: '石川県金沢市を拠点に活動するフォトグラファー・ビデオグラファー',
        inLanguage: 'ja',
        publisher: {
          '@id': `${SITE_URL}/#business`,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/#webpage`,
        url: SITE_URL,
        name: 'KAZU.PHOTO | 石川県金沢市 フォトグラファー・ビデオグラファー',
        description: '石川県金沢市を拠点に活動するフォトグラファーKAZU.PHOTO。家族写真・ウェディング・ポートレート・商業撮影。',
        inLanguage: 'ja',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${SITE_URL}/#business` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'ホーム', item: SITE_URL },
          ],
        },
      },
    ];

    const scriptTags: HTMLScriptElement[] = schemas.map((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `jsonld-home-${schema['@type']}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      return script;
    });

    return () => {
      scriptTags.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  return null;
}

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const contactFormRef = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['hero', 'portfolio', 'projects', 'about', 'contact'];
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactFormRef.current) return;

    setFormStatus('submitting');
    const formData = new FormData(contactFormRef.current);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6jiek7rgrhbthj8mvs0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        contactFormRef.current.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const portfolioCategories = [
    {
      id: 'family',
      title: 'Family',
      subtitle: '家族の絆と日常の輝き',
      size: 'large',
      image: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/23907b1ba35500cef8122d10a5f898c9.jpeg',
    },
    {
      id: 'wedding',
      title: 'Wedding',
      subtitle: '永遠の愛を形に',
      size: 'medium',
      image: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/4425d8989b06c2d1fa718b5322fc7839.jpeg',
    },
    {
      id: 'portrait',
      title: 'Portrait',
      subtitle: '一人ひとりの個性を',
      size: 'medium',
      image: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/2ff324d91bf4926ec8285fd96058bc2c.jpeg',
    },
    {
      id: 'events',
      title: 'Events',
      subtitle: '人が集まる時間と空気',
      size: 'small',
      image: 'https://public.readdy.ai/ai/img_res/96db288ac6831fed26ea376b595bfeed.jpg',
    },
    {
      id: 'commercial',
      title: 'Commercial',
      subtitle: 'ブランドの物語',
      size: 'small',
      image: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/57e934baf6fc84fb1e16e5d9c4b6dee7.jpeg',
    },
  ];

  const projects = [
    {
      id: 'noto',
      title: '能登を回復する',
      subtitle: '復興への歩み',
      description: '2024年より記録開始。\n"能登の復興、海藻、手仕事"\n被災地の人々の暮らしと希望を追い続ける、ドキュメンタリープロジェクト。',
      stat: 'Recover Noto',
      statLabel: '',
      image: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/6db7e93c576bf7524a4f4eb7182d7062.jpeg',
      color: 'bg-stone-100',
    },
    {
      id: 'wajima',
      title: '輪島塗',
      subtitle: '伝統工芸の技と心',
      description: '300年以上の歴史を持つ輪島塗。職人の手仕事と、そこに宿る美意識を記録する。',
      stat: '300年',
      statLabel: '伝統の歴史',
      image: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/4c1d576cd0eec7ce9d20bb73b40b737e.jpeg',
      color: 'bg-stone-50',
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      <HomeJsonLd />
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="flex items-center justify-between px-8 lg:px-16 py-6">
          <Link
            to="/"
            className={`text-xs tracking-[0.2em] font-light transition-colors ${
              scrolled || activeSection !== 'hero' ? 'text-stone-900' : 'text-white'
            }`}
          >
            KAZU.PHOTO
          </Link>
          <div className="hidden md:flex items-center gap-12">
            {[
              { id: 'portfolio', label: 'Portfolio' },
              { id: 'projects', label: 'Projects' },
              { id: 'about', label: 'About' },
              { id: 'contact', label: 'Contact' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-[11px] tracking-[0.2em] font-light transition-colors relative group ${
                  scrolled || activeSection !== 'hero' ? 'text-stone-600' : 'text-white/80'
                } hover:text-stone-900`}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-current transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative h-screen w-full overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/e4341d1a0d62cc6d480c2d41583be8be.jpeg')`,
          }}
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative h-full flex flex-col items-center justify-center text-white">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-extralight tracking-[0.15em] mb-6 drop-shadow-lg">
            KAZU.PHOTO
          </h1>
          <p className="text-xs md:text-sm tracking-[0.3em] font-light opacity-90">
            Photographer / Videografer
          </p>
        </div>
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white/60 text-xl" />
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-32 lg:py-40 px-8 lg:px-16 bg-white">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-20">
          <h2 className="text-4xl md:text-5xl font-extralight tracking-wide text-stone-900 mb-4 md:mb-0">
            Portfolio
          </h2>
          <p className="text-sm text-stone-500 font-light tracking-wide">
            人生の物語を未来に残す
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* Family - Large Card */}
          <Link to="/portfolio/family" className="md:row-span-2 group cursor-pointer">
            <div className="relative h-full min-h-[500px] md:min-h-full rounded-2xl overflow-hidden bg-stone-900">
              <img
                src={portfolioCategories[0].image}
                alt={portfolioCategories[0].title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute top-8 left-8">
                <h3 className="text-2xl font-extralight text-white tracking-wide mb-2">
                  {portfolioCategories[0].title}
                </h3>
                <p className="text-xs text-white/70 tracking-wider">
                  {portfolioCategories[0].subtitle}
                </p>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="text-center">
                  <p className="text-white text-sm tracking-wider mb-2">View Gallery</p>
                  <i className="ri-arrow-right-line text-white text-xl" />
                </div>
              </div>
            </div>
          </Link>

          {/* Wedding */}
          <Link to="/portfolio/wedding" className="group cursor-pointer">
            <div className="relative rounded-2xl overflow-hidden bg-stone-50 shadow-sm hover:shadow-md transition-shadow">
              <div className="p-6 text-center">
                <h3 className="text-lg font-extralight text-stone-900 tracking-wide mb-1">
                  {portfolioCategories[1].title}
                </h3>
              </div>
              <div className="aspect-[4/5] overflow-hidden relative">
                <img
                  src={portfolioCategories[1].image}
                  alt={portfolioCategories[1].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
                  <div className="text-center">
                    <p className="text-white text-sm tracking-wider mb-2">View Gallery</p>
                    <i className="ri-arrow-right-line text-white text-xl" />
                  </div>
                </div>
              </div>
            </div>
          </Link>

          {/* Portrait */}
          <Link to="/portfolio/portrait" className="group cursor-pointer">
            <div className="relative rounded-2xl overflow-hidden bg-stone-50 shadow-sm hover:shadow-md transition-shadow">
              <div className="p-6 text-center">
                <h3 className="text-lg font-extralight text-stone-900 tracking-wide mb-1">
                  {portfolioCategories[2].title}
                </h3>
              </div>
              <div className="aspect-[4/5] overflow-hidden relative">
                <img
                  src={portfolioCategories[2].image}
                  alt={portfolioCategories[2].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
                  <div className="text-center">
                    <p className="text-white text-sm tracking-wider mb-2">View Gallery</p>
                    <i className="ri-arrow-right-line text-white text-xl" />
                  </div>
                </div>
              </div>
            </div>
          </Link>

          {/* Still Life */}
          <Link to="/portfolio/events" className="group cursor-pointer">
            <div className="relative rounded-2xl overflow-hidden">
              <div className="aspect-square overflow-hidden">
                <img
                  src={portfolioCategories[3].image}
                  alt={portfolioCategories[3].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute bottom-4 left-4">
                <h3 className="text-sm font-light text-white tracking-wide drop-shadow-md">
                  {portfolioCategories[3].title}
                </h3>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
                <div className="text-center">
                  <p className="text-white text-sm tracking-wider mb-2">View Gallery</p>
                  <i className="ri-arrow-right-line text-white text-xl" />
                </div>
              </div>
            </div>
          </Link>

          {/* Commercial */}
          <Link to="/portfolio/commercial" className="group cursor-pointer">
            <div className="relative rounded-2xl overflow-hidden">
              <div className="aspect-square overflow-hidden">
                <img
                  src={portfolioCategories[4].image}
                  alt={portfolioCategories[4].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute bottom-4 left-4">
                <h3 className="text-sm font-light text-white tracking-wide drop-shadow-md">
                  {portfolioCategories[4].title}
                </h3>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
                <div className="text-center">
                  <p className="text-white text-sm tracking-wider mb-2">View Gallery</p>
                  <i className="ri-arrow-right-line text-white text-xl" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-32 lg:py-40 px-8 lg:px-16 bg-stone-50">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-20">
          <h2 className="text-4xl md:text-5xl font-extralight tracking-wide text-stone-900 mb-4 md:mb-0">
            Documentary Projects
          </h2>
          <p className="text-sm text-stone-500 font-light tracking-wide">
            能登の文化と人々の記憶を記録する
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((project) => (
            <Link
              key={project.id}
              to={project.id === 'wajima' ? '/portfolio/wajima-nuri' : project.id === 'noto' ? '/portfolio/noto-recovery' : '#'}
              className={`group cursor-pointer rounded-3xl overflow-hidden ${project.color} shadow-sm hover:shadow-lg transition-all duration-500`}
            >
              <div className="relative">
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <button className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/90 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-arrow-right-up-line text-stone-800" />
                </button>
              </div>
              <div className="p-8">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="text-5xl font-extralight text-stone-800">{project.stat}</span>
                  <span className="text-xs text-stone-500 tracking-wider">{project.statLabel}</span>
                </div>
                <h3 className="text-2xl font-light text-stone-900 mb-2">{project.title}</h3>
                <p className="text-sm text-stone-600 mb-4">{project.subtitle}</p>
                <p className="text-sm text-stone-500 leading-relaxed whitespace-pre-line">{project.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 lg:py-40 px-8 lg:px-16 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div className="relative">
            <div className="aspect-[3/4] rounded-3xl overflow-hidden">
              <img
                src="https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/44440d262883f77d01ba6b0e32e0213d.jpeg"
                alt="Photographer"
                className="w-full h-full object-cover grayscale"
              />
            </div>
          </div>
          <div>
            <span className="text-[10px] tracking-[0.3em] text-stone-400 uppercase mb-8 block">
              About
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-stone-900 leading-relaxed mb-8">
              人生の物語を
              <br />
              <span className="italic font-serif">未来に</span>
              <br />
              残すために
            </h2>
            <p className="text-sm text-stone-500 tracking-wide mb-8">
              石川県を拠点に活動するフォトグラファー
            </p>
            <div className="space-y-6 text-stone-600 leading-relaxed">
              <p>
                家族の笑顔、職人の手仕事、地域の伝統。<br />私は人々の暮らしと文化の物語を、<br />写真と映像で記録しています。
              </p>
              <p>
                写真という媒体を通じて、一瞬一瞬に宿る美しさや感情、<br />そして時間の流れを切り取りたい。<br />そんな想いで、日々シャッターを押し続けています。
              </p>
              <p>
                石川県の豊かな自然と文化に囲まれながら、ここでしか出会えない物語を、<br />未来へと残していきたいと思っています。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 lg:py-40 px-8 lg:px-16 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extralight tracking-wide text-stone-900 mb-4">
              Contact
            </h2>
            <p className="text-sm text-stone-500 font-light">
              ご相談やお問い合わせはこちらから
            </p>
          </div>

          <form
            ref={contactFormRef}
            onSubmit={handleContactSubmit}
            data-readdy-form
            className="space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-xs tracking-wider text-stone-500 mb-3">
                  お名前
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs tracking-wider text-stone-500 mb-3">
                  メールアドレス
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs tracking-wider text-stone-500 mb-3">
                ご用件
              </label>
              <select
                name="subject"
                required
                className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors"
              >
                <option value="">選択してください</option>
                <option value="family">家族写真の撮影について</option>
                <option value="wedding">ウェディング撮影について</option>
                <option value="portrait">ポートレート撮影について</option>
                <option value="commercial">商業撮影について</option>
                <option value="other">その他</option>
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-wider text-stone-500 mb-3">
                メッセージ
              </label>
              <textarea
                name="message"
                rows={5}
                maxLength={500}
                required
                className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors resize-none"
              />
              <p className="text-xs text-stone-400 mt-2">500文字以内で入力してください</p>
            </div>
            <div className="text-center pt-8">
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="inline-flex items-center gap-3 px-12 py-4 bg-stone-900 text-white text-sm tracking-wider rounded-full hover:bg-stone-800 transition-colors disabled:opacity-50"
              >
                {formStatus === 'submitting' ? '送信中...' : '送信する'}
                <i className="ri-arrow-right-line" />
              </button>
            </div>
            {formStatus === 'success' && (
              <p className="text-center text-sm text-green-600">お問い合わせありがとうございます。ご連絡いたします。</p>
            )}
            {formStatus === 'error' && (
              <p className="text-center text-sm text-red-600">送信に失敗しました。もう一度お試しください。</p>
            )}
          </form>

          <div className="mt-20 pt-12 border-t border-stone-200">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <p className="text-xs tracking-wider text-stone-400 mb-2">Location</p>
                <p className="text-sm text-stone-600">石川県金沢市</p>
                <p className="text-sm text-stone-500">Kanazawa, Ishikawa</p>
              </div>
              <div className="flex items-center gap-6">
                <a
                  href="https://www.instagram.com/ka91uver/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 transition-colors"
                >
                  <i className="ri-instagram-line text-lg" />
                  Instagram
                </a>
                <a
                  href="https://www.youtube.com/channel/UChcXY6jhxBz8Q74T6IYq2MA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 transition-colors"
                >
                  <i className="ri-youtube-line text-lg" />
                  YouTube
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-16 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-extralight tracking-wide mb-4">KAZU.PHOTO</h3>
              <p className="text-xs text-white/60 tracking-wider">
                Photographer / Videografer
              </p>
            </div>
            <div>
              <p className="text-xs tracking-wider text-white/40 mb-4">Navigate</p>
              <nav className="space-y-3">
                {['Portfolio', 'Projects', 'About', 'Contact'].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="block text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {item}
                  </button>
                ))}
              </nav>
            </div>
            <div>
              <p className="text-xs tracking-wider text-white/40 mb-4">Connect</p>
              <a
                href="https://www.instagram.com/ka91uver/"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-white/70 hover:text-white transition-colors mb-2"
              >
                Instagram
              </a>
              <a
                href="https://www.youtube.com/channel/UChcXY6jhxBz8Q74T6IYq2MA"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm text-white/70 hover:text-white transition-colors"
              >
                YouTube
              </a>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40">© 2025 KAZU.PHOTO</p>
            <p className="text-xs text-white/40">人生の物語を未来に残す</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
